import type { BridgeError } from "./protocol.ts";

const OP_FAIL_RE = /^Op (\d+) \(([^)]+)\) failed:\s*(.*)$/s;

const classifyCode = (message: string, name: string): string => {
  if (name === "BatchRejected" || message.includes("BatchRejected")) return "BatchRejected";
  if (name === "BudgetExceeded" || message.includes("Run exceeded")) return "BudgetExceeded";
  if (message.includes("Can't find variable: PowerPoint") || message.includes("PowerPoint is not defined")) {
    return "PowerPointNotInSandbox";
  }
  if (message.includes("PropertyNotLoaded")) return "PropertyNotLoaded";
  if (message.includes("InvalidArgument")) return "InvalidArgument";
  if (message.includes("GeneralException")) return "GeneralException";
  if (message.includes("ItemNotFound") || message.includes("SlideNotFound")) return "NotFound";
  if (message.includes("AccessDenied")) return "AccessDenied";
  if (message.includes("The current selection is not supported")) return "SelectionUnsupported";
  return name && name !== "Error" ? name : "OfficeJsError";
};

const hintFor = (
  code: string,
  op: string | undefined,
): { hint: string; nextStep: string } => {
  switch (code) {
    case "PropertyNotLoaded":
      return {
        hint: "A proxy property was read before load+sync. Typed ops load for you; oj.get/oj.call need an explicit load string for scalars.",
        nextStep:
          "Do not retry the same oj.* path. Call pp.getSlideContents(slideIndex) (or oj.get with load), then retry using ids/types from that snapshot.",
      };
    case "InvalidArgument":
      return {
        hint:
          op === "oj.set" || op === "oj.get" || op === "oj.call"
            ? "Likely a type-gate: textFrame/font/fill used on a non-text or unsupported shape."
            : "An Office.js argument was invalid for this object type or host.",
        nextStep:
          "Call pp.getSlideContents(slideIndex), check shape.type, and only use text APIs on Placeholder|TextBox|GeometricShape|Callout. Prefer pp.addShapes for new content.",
      };
    case "GeneralException":
      return {
        hint: "Host rejected the call — often wrong shape type (placeholderFormat/group on a non-matching shape) or a no-op API on this platform.",
        nextStep:
          "Re-read with pp.getSlideContents, gate by type, and avoid unsupported APIs. If this is a chart/SmartArt/notes request, switch to the python-pptx escape hatch instead of retrying oj.*.",
      };
    case "NotFound":
      return {
        hint: "Slide or shape id/index no longer exists (indexes shift after insert/delete).",
        nextStep: "Call pp.countSlides / pp.getOutline, then address shapes by id from getSlideContents — do not reuse stale indexes.",
      };
    case "PowerPointNotInSandbox":
      return {
        hint: "PowerPoint.run / Office are not defined in execute_office_js_code. Only pp.* works in the sandbox.",
        nextStep: "Rewrite using pp.batch / pp.addShapes / pp.getSlideContents. Never emit PowerPoint.run(...).",
      };
    case "BatchRejected":
      return {
        hint: "pp.batch was rejected before running (too many ops or invalid op shape).",
        nextStep: `Split work: max 50 ops per pp.batch. Prefer one pp.addShapes call for construction instead of many oj.* ops.`,
      };
    case "BudgetExceeded":
      return {
        hint: "execute_office_js_code hit the time budget. Partial writes from earlier synced ops may already be on the slide.",
        nextStep:
          "Do not resubmit the same large script. Re-read the slide, then apply a small patch batch only.",
      };
    case "AccessDenied":
      return {
        hint: "PowerPoint blocked the call (often slideshow mode or a protected view).",
        nextStep: "Ask the user to exit slideshow / unprotected view, then retry once.",
      };
    case "SelectionUnsupported":
      return {
        hint: "Current selection cannot be read as text.",
        nextStep: "Branch on unsupported selection; use getSelectedSlides via oj.call or ask the user to select a text range.",
      };
    default:
      return {
        hint: "Office.js call failed. Blind retries usually make this worse.",
        nextStep:
          "Read the failing op index, call pp.getSlideContents on the relevant slide once, then change strategy — do not repeat the same oj.* sequence.",
      };
  }
};

export const enrichBridgeError = (error: BridgeError): BridgeError => {
  const message = error.message;
  const opMatch = OP_FAIL_RE.exec(message);
  const failingOpIndex =
    error.failingOpIndex ??
    (opMatch ? Number.parseInt(opMatch[1] ?? "", 10) : undefined);
  const op = error.op ?? (opMatch?.[2] && opMatch[2].length > 0 ? opMatch[2] : undefined);
  const inner = opMatch?.[3] ?? message;
  const code = error.code ?? classifyCode(inner, error.name);
  const { hint, nextStep } = hintFor(code, op);

  return {
    name: code === "PowerPointNotInSandbox" || code === "BatchRejected" || code === "BudgetExceeded"
      ? code
      : error.name || "Error",
    message,
    stack: error.stack,
    code,
    failingOpIndex: Number.isFinite(failingOpIndex) ? failingOpIndex : undefined,
    op,
    hint: error.hint ?? hint,
    nextStep: error.nextStep ?? nextStep,
  };
};

export const batchRejectedError = (detail: string): BridgeError =>
  enrichBridgeError({
    name: "BatchRejected",
    message: `BatchRejected: ${detail}`,
    code: "BatchRejected",
  });
